import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock, Layers, Lock, LockOpen, Search } from 'lucide-react';
import {
  calcularInicioPlanejadoLote,
  chaveLotePersistencia,
  extrairHoraMinutoDateTimeLocal,
  extrairPecasBrutasDoLote,
  listarCardsTempoCorteDoLote,
  mesclarLinhasPersistidas,
  somarMinutosADataHoraLocal,
  totalMinutosLinhas,
  unificarPecasLote,
  type LinhaTempoProducaoPeca,
  type RegistroTempoProducaoPecaLote,
} from '../../utils/tempoProducaoPecaLote';
import { cardTempoCorteFoiExcluidoManual } from '../../utils/cardsTempoCorteExcluidos';
import {
  lerRegistrosTempoProducaoPeca,
  salvarRegistroTempoProducaoPecaLote,
} from '../../utils/tempoProducaoPecaPersistencia';
import { InputTempoHms } from './InputTempoHms';

interface LoteProdutoRef {
  lote: string;
  produtoNome: string;
  qtd: number;
  dataHoraInicioCorte?: string;
}

interface ProdutoTempoCorteRef {
  id?: string;
  nome?: string;
  lote?: string;
  qtdProduto?: number;
  dataCriacaoISO?: string;
  salvoEm?: string;
  itens?: Array<{
    descricao?: string;
    qtd?: number;
    medida?: string;
    material?: string;
    metrosQuadrados?: number;
    selecionado?: boolean;
    produtoOrigem?: string;
  }>;
}

interface TempoProducaoPecaSectionProps {
  lotesProdutos: LoteProdutoRef[];
  produtosTempoCorteSalvos: ProdutoTempoCorteRef[];
  formatarTempo: (minutos: number) => string;
  onAplicarProducaoLote: (loteDigitado: string, inicio: string, termino: string) => void;
}

export default function TempoProducaoPecaSection({
  lotesProdutos,
  produtosTempoCorteSalvos,
  formatarTempo,
  onAplicarProducaoLote,
}: TempoProducaoPecaSectionProps) {
  const [loteDigitado, setLoteDigitado] = useState('');
  const [linhas, setLinhas] = useState<LinhaTempoProducaoPeca[]>([]);
  const [tempoPercaMinutos, setTempoPercaMinutos] = useState(0);
  /** Bloqueio só nesta aba (tempos/perca). Sync do Tempo de Corte continua. */
  const [edicaoBloqueada, setEdicaoBloqueada] = useState(false);
  const [registrosSalvos, setRegistrosSalvos] = useState<Record<string, RegistroTempoProducaoPecaLote>>({});
  const carregouRef = useRef(false);
  const ultimoAplicadoRef = useRef('');
  const registrosSalvosRef = useRef(registrosSalvos);
  const edicaoBloqueadaRef = useRef(edicaoBloqueada);
  registrosSalvosRef.current = registrosSalvos;
  edicaoBloqueadaRef.current = edicaoBloqueada;

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const mapa = await lerRegistrosTempoProducaoPeca();
      if (cancelado) return;
      setRegistrosSalvos(mapa);
      carregouRef.current = true;
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const loteChave = useMemo(
    () => (loteDigitado.trim() ? chaveLotePersistencia(loteDigitado) : ''),
    [loteDigitado]
  );

  const cardsDoLote = useMemo(
    () =>
      listarCardsTempoCorteDoLote(produtosTempoCorteSalvos, loteDigitado, (c) =>
        cardTempoCorteFoiExcluidoManual(c)
      ),
    [produtosTempoCorteSalvos, loteDigitado]
  );

  /** Assinatura das peças selecionadas — força recarga instantânea ao marcar/desmarcar. */
  const assinaturaSelecaoCards = useMemo(
    () =>
      cardsDoLote
        .map((c) => {
          const sel = Array.isArray(c.itens)
            ? c.itens.map((it) => (it.selecionado === false ? '0' : '1')).join('')
            : '';
          return `${c.id || c.nome}:${sel}:${Array.isArray(c.itens) ? c.itens.length : 0}`;
        })
        .join('|'),
    [cardsDoLote]
  );

  const inicioPlanejado = useMemo(
    () => (loteDigitado.trim() ? calcularInicioPlanejadoLote(loteDigitado, lotesProdutos) : ''),
    [loteDigitado, lotesProdutos]
  );

  const loteAnteriorRef = useRef('');

  const recarregarLinhasDoLote = useCallback(
    (lote: string, opts?: { preservarTempos?: boolean }) => {
      const chave = chaveLotePersistencia(lote);
      const mapa = registrosSalvosRef.current;
      const pecas = extrairPecasBrutasDoLote(lote, produtosTempoCorteSalvos, lotesProdutos);
      const unificadas = unificarPecasLote(pecas);
      const salvo = mapa[chave];
      const mescladas = mesclarLinhasPersistidas(unificadas, salvo?.linhas);

      setLinhas((prev) => {
        if (!opts?.preservarTempos || prev.length === 0) return mescladas;
        const tempos = new Map(prev.map((l) => [l.chaveUnificacao, l.tempoLinhaMinutos]));
        return mescladas.map((l) => ({
          ...l,
          tempoLinhaMinutos: tempos.get(l.chaveUnificacao) ?? l.tempoLinhaMinutos,
        }));
      });

      if (!opts?.preservarTempos) {
        setTempoPercaMinutos(Number(salvo?.tempoPercaMinutos) || 0);
        setEdicaoBloqueada(Boolean(salvo?.edicaoBloqueada));
      }
    },
    [lotesProdutos, produtosTempoCorteSalvos]
  );

  useEffect(() => {
    if (!carregouRef.current || !loteDigitado.trim()) {
      setLinhas([]);
      setEdicaoBloqueada(false);
      loteAnteriorRef.current = '';
      return;
    }
    const mesmoLote = loteAnteriorRef.current === loteDigitado;
    loteAnteriorRef.current = loteDigitado;
    // Mesmo lote: preserva tempos digitados nas linhas que continuam existindo.
    // seleção/exclusão de cards: assinatura muda → lista recalcula na hora.
    // Troca de lote: restaura flag de bloqueio do registro salvo.
    if (!mesmoLote) {
      const salvo = registrosSalvosRef.current[chaveLotePersistencia(loteDigitado)];
      setEdicaoBloqueada(Boolean(salvo?.edicaoBloqueada));
    }
    recarregarLinhasDoLote(loteDigitado, { preservarTempos: mesmoLote });
  }, [
    loteDigitado,
    assinaturaSelecaoCards,
    produtosTempoCorteSalvos,
    lotesProdutos,
    recarregarLinhasDoLote,
  ]);

  const totalPecasMinutos = useMemo(() => totalMinutosLinhas(linhas), [linhas]);
  const totalDuracaoMin = useMemo(
    () => totalPecasMinutos + tempoPercaMinutos,
    [totalPecasMinutos, tempoPercaMinutos]
  );
  const terminoProducao = useMemo(() => {
    if (!inicioPlanejado) return '';
    return somarMinutosADataHoraLocal(inicioPlanejado, totalDuracaoMin);
  }, [inicioPlanejado, totalDuracaoMin]);

  const persistir = useCallback(
    async (linhasAtual: LinhaTempoProducaoPeca[], percaMin: number, bloqueada?: boolean) => {
      if (!loteChave || !loteDigitado.trim()) return;
      const registro: RegistroTempoProducaoPecaLote = {
        loteChave,
        loteDigitado: loteDigitado.trim().toUpperCase(),
        linhas: linhasAtual,
        tempoPercaMinutos: percaMin,
        dataHoraInicioPlanejado: inicioPlanejado,
        dataHoraTerminoProducaoCalculado: terminoProducao,
        atualizadoEm: new Date().toISOString(),
        edicaoBloqueada:
          typeof bloqueada === 'boolean' ? bloqueada : edicaoBloqueadaRef.current,
      };
      await salvarRegistroTempoProducaoPecaLote(registro);
      setRegistrosSalvos((prev) => ({ ...prev, [loteChave]: registro }));
    },
    [inicioPlanejado, loteChave, loteDigitado, terminoProducao]
  );

  useEffect(() => {
    if (!carregouRef.current || !loteChave) return;
    const t = window.setTimeout(() => {
      void persistir(linhas, tempoPercaMinutos);
    }, 500);
    return () => window.clearTimeout(t);
  }, [linhas, tempoPercaMinutos, loteChave, persistir]);

  useEffect(() => {
    if (!loteDigitado.trim() || !inicioPlanejado || !terminoProducao) return;
    const chaveApply = `${loteChave}|${inicioPlanejado}|${terminoProducao}|${totalPecasMinutos}|${tempoPercaMinutos}`;
    if (ultimoAplicadoRef.current === chaveApply) return;
    if (totalPecasMinutos <= 0 && tempoPercaMinutos <= 0) return;
    ultimoAplicadoRef.current = chaveApply;
    onAplicarProducaoLote(loteDigitado.trim(), inicioPlanejado, terminoProducao);
  }, [
    inicioPlanejado,
    terminoProducao,
    loteDigitado,
    loteChave,
    onAplicarProducaoLote,
    totalPecasMinutos,
    tempoPercaMinutos,
  ]);

  const lotesDisponiveis = useMemo(() => {
    const set = new Set<string>();
    lotesProdutos.forEach((lp) => {
      const n = String(lp.lote || '').trim().toUpperCase();
      if (n) set.add(n);
    });
    produtosTempoCorteSalvos.forEach((p) => {
      const n = String(p.lote || '').trim().toUpperCase();
      if (n) set.add(n);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
  }, [lotesProdutos, produtosTempoCorteSalvos]);

  const atualizarTempoLinha = (chave: string, minutos: number) => {
    if (edicaoBloqueadaRef.current) return;
    setLinhas((prev) =>
      prev.map((l) =>
        l.chaveUnificacao === chave ? { ...l, tempoLinhaMinutos: minutos } : l
      )
    );
  };

  const alternarBloqueioEdicao = () => {
    if (!loteDigitado.trim() || !loteChave) return;
    const proximo = !edicaoBloqueada;
    setEdicaoBloqueada(proximo);
    void persistir(linhas, tempoPercaMinutos, proximo);
  };

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-black flex items-center gap-2">
        <Clock className="w-5 h-5 text-[#009b3a]" />
        TEMPO DE CORTE POR PEÇA EM PRODUÇÃO
      </h3>
      <p className="text-sm text-neutral-600">
        Digite o lote para reunir as peças selecionadas de <strong>todos</strong> os cards do Tempo de Corte
        desse lote (nenhum card fica de fora). A quantidade de cada linha é{' '}
        <strong>(qtd na tabela do TC × qtd do produto no cadastro de Lotes)</strong> quando o produto casa;
        se não achar no cadastro, usa a qtd do card. Depois unifica por <strong>medida + material</strong>
        (ex.: INF e SUP na mesma medida viram uma linha). Informe o tempo de cada linha e a perca; o término
        de produção é aplicado nos cards do lote.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#fafbfc] rounded-xl border border-[#009b3a]/15 p-4">
          <label className="block text-xs font-medium text-neutral-600 mb-1 flex items-center gap-1">
            <Search className="w-3.5 h-3.5" /> Número do lote
          </label>
          <input
            type="text"
            list="lotes-tempo-producao-peca"
            value={loteDigitado}
            onChange={(e) => setLoteDigitado(e.target.value.toUpperCase())}
            placeholder="Ex: 8798"
            className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/28 text-sm font-semibold uppercase"
          />
          <datalist id="lotes-tempo-producao-peca">
            {lotesDisponiveis.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
          <p className="mt-1 text-[11px] text-neutral-500">
            {cardsDoLote.length} card(s) TC · {linhas.length} linha(s) unificada(s)
          </p>
          {loteDigitado.trim() && (
            <button
              type="button"
              onClick={alternarBloqueioEdicao}
              className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                edicaoBloqueada
                  ? 'bg-amber-100 border-amber-400 text-amber-900 hover:bg-amber-200'
                  : 'bg-white border-[#009b3a]/35 text-[#006b28] hover:bg-[#e8f5ec]'
              }`}
              title={
                edicaoBloqueada
                  ? 'Desbloquear para editar tempos nesta aba'
                  : 'Bloquear edição de tempos neste lote (só nesta aba)'
              }
            >
              {edicaoBloqueada ? (
                <>
                  <LockOpen className="w-3.5 h-3.5" /> Desbloquear edição deste lote
                </>
              ) : (
                <>
                  <Lock className="w-3.5 h-3.5" /> Bloquear edição deste lote
                </>
              )}
            </button>
          )}
          {edicaoBloqueada && (
            <p className="mt-1.5 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Tempos e perca travados neste lote. Alterações do Tempo de Corte (peças/qtd/medida) ainda entram
              automaticamente.
            </p>
          )}
          {cardsDoLote.length > 0 && (
            <ul className="mt-2 max-h-24 overflow-y-auto text-[10px] text-neutral-600 space-y-0.5">
              {cardsDoLote.map((c, i) => {
                const nItens = Array.isArray(c.itens)
                  ? c.itens.filter((it) => it.selecionado !== false).length
                  : 0;
                return (
                  <li key={String(c.id || `${c.nome}-${i}`)}>
                    <span className="font-semibold text-neutral-800">{c.nome || '(sem nome)'}</span>
                    <span className="text-neutral-400"> · {nItens} peça(s)</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-xl border-2 border-[#009b3a]/28 bg-[#f0faf3] p-4">
          <label className="block text-xs font-medium text-green-700 mb-1">Início (Planejado / Produção)</label>
          <input
            type="datetime-local"
            value={inicioPlanejado}
            readOnly
            className="w-full px-2 py-1.5 rounded border border-[#009b3a]/35 bg-[#e8f5ec] text-sm"
          />
        </div>

        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <label className="block text-xs font-medium text-amber-800 mb-1">Término Produção (calculado)</label>
          <input
            type="datetime-local"
            value={terminoProducao}
            readOnly
            className="w-full px-2 py-1.5 rounded border border-amber-400 bg-amber-100/80 text-sm font-semibold"
          />
          <p className="mt-1 text-[10px] text-amber-700">
            <span className="font-semibold">
              {extrairHoraMinutoDateTimeLocal(inicioPlanejado)} + {formatarTempo(totalDuracaoMin)} ={' '}
              {terminoProducao ? extrairHoraMinutoDateTimeLocal(terminoProducao) : '—'}
            </span>
            <span className="block mt-0.5 text-amber-600">
              Peças {formatarTempo(totalPecasMinutos)} + perca {formatarTempo(tempoPercaMinutos)} — aplicado nos
              cards do lote
            </span>
          </p>
        </div>
      </div>

      <div className="bg-[#fafbfc] rounded-xl border border-[#009b3a]/15 p-4 max-w-md">
        <label className="block text-xs font-medium text-neutral-700 mb-1">
          Tempo de PERCA (troca de material / parada avulsa)
        </label>
        <InputTempoHms
          minutos={tempoPercaMinutos}
          disabled={edicaoBloqueada}
          onChange={(min) => {
            if (edicaoBloqueadaRef.current) return;
            setTempoPercaMinutos(min);
          }}
        />
        <p className="mt-1 text-[10px] text-neutral-500">Hora · minuto · segundo (campos separados)</p>
      </div>

      {loteDigitado.trim() && linhas.length === 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
          {cardsDoLote.length === 0 ? (
            <>
              Nenhum card do Tempo de Corte Exato por Pontuação encontrado para o lote <strong>{loteDigitado}</strong>.
              Confira se o card foi salvo com este número de lote.
            </>
          ) : (
            <>
              {cardsDoLote.length} card(s) encontrado(s), mas nenhuma peça selecionada com quantidade.
              Abra o card no Tempo de Corte Exato por Pontuação e marque as peças (checkbox) antes de salvar.
            </>
          )}
        </div>
      )}

      {linhas.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[#009b3a]/15">
          <table className="w-full text-sm border-collapse min-w-max">
            <thead className="bg-[#e4e6ec]">
              <tr>
                <th className="px-2 py-2 text-left border border-[#009b3a]/15">DESCRIÇÃO</th>
                <th className="px-2 py-2 text-center border border-[#009b3a]/15">QTD</th>
                <th className="px-2 py-2 text-center border border-[#009b3a]/15">MEDIDA</th>
                <th className="px-2 py-2 text-center border border-[#009b3a]/15">METROS²</th>
                <th className="px-2 py-2 text-center border border-[#009b3a]/15">MATERIAL</th>
                <th className="px-2 py-2 text-center border border-[#009b3a]/15 bg-[#eef2f8]">
                  TEMPO TOTAL LINHA⁸
                </th>
                <th className="px-2 py-2 text-left border border-[#009b3a]/15">Origem</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <tr key={linha.chaveUnificacao} className="hover:bg-[#f0faf3]/50">
                  <td className="px-2 py-1.5 border font-medium">{linha.descricaoExibicao}</td>
                  <td className="px-2 py-1.5 border text-center font-bold">{linha.qtd}</td>
                  <td className="px-2 py-1.5 border text-center text-xs">{linha.medida}</td>
                  <td className="px-2 py-1.5 border text-center">
                    {linha.metrosQuadrados.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 border text-center text-xs">{linha.material}</td>
                  <td className="px-2 py-1.5 border bg-[#eef2f8]">
                    <InputTempoHms
                      compact
                      disabled={edicaoBloqueada}
                      minutos={linha.tempoLinhaMinutos}
                      onChange={(min) => atualizarTempoLinha(linha.chaveUnificacao, min)}
                    />
                  </td>
                  <td className="px-2 py-1.5 border text-xs text-neutral-600 max-w-[12rem] truncate" title={linha.origens.join(', ')}>
                    {linha.origens.join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#e8f5ec] font-semibold">
                <td colSpan={5} className="px-2 py-2 border text-right">
                  Total peças + perca
                </td>
                <td className="px-2 py-2 border text-center font-mono">
                  {formatarTempo(totalDuracaoMin)}
                </td>
                <td className="border" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {Object.keys(registrosSalvos).length > 0 && (
        <div className="rounded-xl border border-[#009b3a]/15 bg-[#fafbfc] p-4">
          <h4 className="text-sm font-semibold text-black mb-2 flex items-center gap-2">
            <Layers className="w-4 h-4" /> Lotes já registrados nesta aba
          </h4>
          <div className="flex flex-wrap gap-2">
            {Object.values(registrosSalvos)
              .sort((a, b) => b.atualizadoEm.localeCompare(a.atualizadoEm))
              .slice(0, 24)
              .map((r) => (
                <button
                  key={r.loteChave}
                  type="button"
                  onClick={() => setLoteDigitado(r.loteDigitado)}
                  className={`px-3 py-1 rounded-full text-xs border inline-flex items-center gap-1 ${
                    r.loteChave === loteChave
                      ? 'bg-[#009b3a] text-white border-[#009b3a]'
                      : 'bg-white border-[#009b3a]/25 hover:bg-[#e8f5ec]'
                  }`}
                >
                  {r.edicaoBloqueada ? <Lock className="w-3 h-3 shrink-0 opacity-80" /> : null}
                  {r.loteDigitado}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
